import React from "react";
import { Grid, GridColumn, GridToolbar } from "@progress/kendo-react-grid";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { Input, NumericTextBox, Checkbox } from "@progress/kendo-react-inputs";

const STATUS_OPTIONS = {
  frame: ["", "CURRENT", "NEW", "PROMO/TD", "GOOD SAMPL", "DROPPED", "DELETE", "CASE", "SAMPLE"],
  fabric: ["", "CURRENT", "NEW", "TODROP", "DROPPED"]
};
const TOP_OPTIONS = [50, 100, 200, 500];

function lsKey(entity) {
  return "dv-search-fields:" + entity;
}

function loadSavedFields(entity) {
  try {
    var raw = localStorage.getItem(lsKey(entity));
    if (!raw) return null;
    var arr = JSON.parse(raw);
    return Array.isArray(arr) && arr.length > 0 ? arr : null;
  } catch (e) {
    return null;
  }
}

function saveFields(entity, fields) {
  try {
    localStorage.setItem(lsKey(entity), JSON.stringify(fields));
  } catch (e) {
    // localStorage quota / disabled — silently no-op
  }
}

// Format a single cell value for display based on the field's catalog type.
function formatCell(value, type) {
  if (value === null || value === undefined || value === "") return "";
  if (type === "bool") {
    var s = String(value).toUpperCase();
    if (s === "Y" || s === "TRUE" || s === "1") return "✓";
    if (s === "N" || s === "FALSE" || s === "0") return "✗";
    return s;
  }
  if (type === "date") {
    try {
      var d = new Date(value);
      if (isNaN(d.getTime())) return String(value);
      return d.toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" });
    } catch (e) {
      return String(value);
    }
  }
  if (type === "decimal" || type === "int") {
    var n = Number(value);
    return isNaN(n) ? String(value) : n.toLocaleString();
  }
  return String(value);
}

export default class DataverseSearchGrid extends React.Component {
  constructor(props) {
    super(props);
    this._export = null;
    this.state = {
      entity: "frame",
      sku: "",
      category: "",
      status: "",
      visibleOnly: false,
      top: 200,
      categories: [],
      fieldCatalog: null,           // { entity, fields:[], defaultFields:[] }
      selectedFields: [],           // logical names
      showColumnPicker: false,
      rows: [],
      totalCount: null,
      returnedCount: 0,
      elapsedMs: null,
      queryUrl: "",
      errorMessage: null,
      loading: false,
      hasSearched: false,
      sort: [],
      skip: 0,
      take: 25
    };
  }

  componentDidMount() {
    this.loadEntity(this.state.entity);
  }

  // Pulls field catalog + categories for the current entity. Restores any
  // saved per-entity column selection from localStorage; otherwise falls back
  // to the catalog's defaultFields.
  loadEntity = (entity) => {
    this.setState({
      entity: entity,
      fieldCatalog: null,
      categories: [],
      selectedFields: [],
      rows: [],
      totalCount: null,
      returnedCount: 0,
      hasSearched: false,
      errorMessage: null
    });

    Promise.all([
      fetch("/api/Admin/Dataverse/Fields?entity=" + entity).then(r => r.json()),
      fetch("/api/Admin/Dataverse/Categories?entity=" + entity).then(r => r.json())
    ]).then((res) => {
      var catalog = res[0];
      var categories = Array.isArray(res[1]) ? res[1] : [];
      var saved = loadSavedFields(entity);
      var selected = saved || (catalog.defaultFields || []).slice();
      this.setState({
        fieldCatalog: catalog,
        categories: categories,
        selectedFields: selected
      });
    }).catch(err => {
      console.error("Failed to load Dataverse metadata:", err);
      this.setState({ errorMessage: "Failed to load field catalog / categories." });
    });
  };

  onEntityChange = (e) => {
    var entity = e.target.value;
    this.loadEntity(entity);
  };

  onSearch = () => {
    var s = this.state;
    var params = new URLSearchParams();
    params.append("entity", s.entity);
    if (s.sku && s.sku.trim()) params.append("sku", s.sku.trim());
    if (s.category) params.append("category", s.category);
    if (s.status) params.append("status", s.status);
    if (s.entity === "frame" && s.visibleOnly) params.append("visibleOnly", "true");
    if (s.selectedFields.length > 0) params.append("fields", s.selectedFields.join(","));
    params.append("top", String(s.top));

    this.setState({ loading: true, errorMessage: null });

    fetch("/api/Admin/Dataverse/Search?" + params.toString())
      .then(r => r.json())
      .then(d => {
        if (!d.success) {
          this.setState({
            loading: false,
            hasSearched: true,
            rows: [],
            totalCount: null,
            returnedCount: 0,
            elapsedMs: d.elapsedMs,
            queryUrl: d.queryUrl || "",
            errorMessage: d.errorMessage || "Search failed."
          });
          return;
        }
        this.setState({
          loading: false,
          hasSearched: true,
          rows: d.rows || [],
          totalCount: d.totalCount,
          returnedCount: d.returnedCount,
          elapsedMs: d.elapsedMs,
          queryUrl: d.queryUrl || "",
          errorMessage: null,
          skip: 0
        });
      })
      .catch(err => {
        console.error(err);
        this.setState({ loading: false, errorMessage: "Network error — check console." });
      });
  };

  onResetFilters = () => {
    this.setState({
      sku: "",
      category: "",
      status: "",
      visibleOnly: false,
      top: 200
    });
  };

  toggleField = (logicalName) => {
    var sel = this.state.selectedFields.slice();
    var idx = sel.indexOf(logicalName);
    if (idx >= 0) sel.splice(idx, 1);
    else sel.push(logicalName);
    this.setState({ selectedFields: sel });
    saveFields(this.state.entity, sel);
  };

  resetFieldsToDefault = () => {
    var defaults = ((this.state.fieldCatalog && this.state.fieldCatalog.defaultFields) || []).slice();
    this.setState({ selectedFields: defaults });
    saveFields(this.state.entity, defaults);
  };

  exportExcel = () => {
    if (this._export) this._export.save(this.state.rows);
  };

  onDataStateChange = (e) => {
    this.setState({
      skip: e.dataState.skip,
      take: e.dataState.take,
      sort: e.dataState.sort
    });
  };

  renderColumnPicker() {
    if (!this.state.showColumnPicker || !this.state.fieldCatalog) return null;
    var fields = this.state.fieldCatalog.fields || [];
    var selected = this.state.selectedFields;
    // Group by Group attribute
    var groups = {};
    fields.forEach(f => {
      var g = f.group || "Misc";
      if (!groups[g]) groups[g] = [];
      groups[g].push(f);
    });
    var order = ["Identity", "Status", "Imagery", "Dimensions", "Materials", "Marketing", "Misc"];
    return (
      <div className="dv-column-picker">
        <div className="dv-column-picker-header">
          <span>{selected.length} of {fields.length} columns</span>
          <button type="button" className="dv-link-button" onClick={this.resetFieldsToDefault}>Reset to default</button>
          <button type="button" className="dv-close-button" onClick={() => this.setState({ showColumnPicker: false })}>✕</button>
        </div>
        <div className="dv-column-picker-body">
          {order.filter(g => groups[g]).map(group => (
            <div key={group} className="dv-column-picker-group">
              <h4>{group}</h4>
              <div className="dv-column-picker-grid">
                {groups[group].map(f => (
                  <label key={f.logicalName} className="dv-column-picker-item">
                    <input
                      type="checkbox"
                      checked={selected.indexOf(f.logicalName) >= 0}
                      onChange={() => this.toggleField(f.logicalName)}
                    />
                    <span>{f.friendlyName}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderGridColumns() {
    var catalog = this.state.fieldCatalog;
    if (!catalog) return null;
    var byName = {};
    (catalog.fields || []).forEach(f => { byName[f.logicalName] = f; });
    return this.state.selectedFields
      .map(name => byName[name])
      .filter(Boolean)
      .map(f => (
        <GridColumn
          key={f.logicalName}
          field={f.logicalName}
          title={f.friendlyName}
          cell={(p) => {
            var v = p.dataItem[f.logicalName];
            var formatted = formatCell(v, f.type);
            var cls = "dv-cell";
            if (f.type === "path") cls += " dv-cell-path";
            if (f.type === "decimal" || f.type === "int") cls += " dv-cell-num";
            return (<td className={cls} title={v === null || v === undefined ? "" : String(v)}>{formatted}</td>);
          }}
        />
      ));
  }

  render() {
    var s = this.state;
    var statusOpts = STATUS_OPTIONS[s.entity] || [];

    var sortedRows = s.rows.slice();
    if (s.sort && s.sort.length > 0) {
      var sortField = s.sort[0].field;
      var dir = s.sort[0].dir === "asc" ? 1 : -1;
      sortedRows.sort((a, b) => {
        var av = a[sortField];
        var bv = b[sortField];
        if (av === bv) return 0;
        if (av === null || av === undefined) return -1 * dir;
        if (bv === null || bv === undefined) return 1 * dir;
        return av > bv ? dir : -dir;
      });
    }
    var pagedRows = sortedRows.slice(s.skip, s.skip + s.take);

    return (
      <div className="dv-search">
        <div className="dv-search-toolbar">
          <div className="dv-toolbar-row">
            <label className="dv-entity-radio">
              <input type="radio" name="dv-entity" value="frame" checked={s.entity === "frame"} onChange={this.onEntityChange} />
              <span>Frame</span>
            </label>
            <label className="dv-entity-radio">
              <input type="radio" name="dv-entity" value="fabric" checked={s.entity === "fabric"} onChange={this.onEntityChange} />
              <span>Fabric</span>
            </label>
          </div>

          <div className="dv-toolbar-row dv-toolbar-filters">
            <div className="dv-filter-field">
              <label>SKU contains</label>
              <Input value={s.sku} onChange={e => this.setState({ sku: e.value })} placeholder="e.g. 8182" />
            </div>
            <div className="dv-filter-field">
              <label>Category</label>
              <DropDownList
                data={["", ...s.categories]}
                value={s.category}
                onChange={e => this.setState({ category: e.target.value })}
                defaultItem=""
                style={{ width: "100%" }}
              />
            </div>
            <div className="dv-filter-field">
              <label>Status</label>
              <DropDownList
                data={statusOpts}
                value={s.status}
                onChange={e => this.setState({ status: e.target.value })}
                style={{ width: "100%" }}
              />
            </div>
            {s.entity === "frame" && (
              <div className="dv-filter-field dv-filter-checkbox">
                <Checkbox
                  checked={s.visibleOnly}
                  onChange={e => this.setState({ visibleOnly: e.value })}
                  label="Visible only (ISVISIBLE=Y)"
                />
              </div>
            )}
            <div className="dv-filter-field dv-filter-top">
              <label>Top</label>
              <DropDownList
                data={TOP_OPTIONS}
                value={s.top}
                onChange={e => this.setState({ top: e.target.value })}
                style={{ width: "85px" }}
              />
            </div>
            <div className="dv-filter-actions">
              <Button onClick={this.onSearch} themeColor="primary" disabled={s.loading}>
                {s.loading ? "Searching..." : "Search"}
              </Button>
              <Button onClick={this.onResetFilters} disabled={s.loading}>Reset</Button>
            </div>
          </div>

          <div className="dv-toolbar-row dv-toolbar-meta">
            <button
              type="button"
              className="dv-link-button"
              onClick={() => this.setState({ showColumnPicker: !s.showColumnPicker })}
            >
              {s.showColumnPicker ? "▴ Hide columns" : "▾ Customize columns"} ({s.selectedFields.length} of {s.fieldCatalog ? s.fieldCatalog.fields.length : 0})
            </button>
            <span className="dv-status-line">
              {s.hasSearched && !s.errorMessage && (
                <>
                  Showing {s.returnedCount.toLocaleString()} of {s.totalCount != null ? s.totalCount.toLocaleString() : "?"} —{" "}
                  Dataverse live query {s.elapsedMs != null ? s.elapsedMs + "ms" : ""}
                  {s.totalCount != null && s.totalCount > s.returnedCount && (
                    <span className="dv-cap-warning"> · capped at top {s.top}, narrow filters to see more</span>
                  )}
                </>
              )}
              {s.errorMessage && <span className="dv-error">{s.errorMessage}</span>}
            </span>
          </div>

          {this.renderColumnPicker()}
        </div>

        {s.hasSearched && s.rows.length === 0 && !s.errorMessage && (
          <div className="dv-empty">No rows match the current filters.</div>
        )}

        {s.rows.length > 0 && (
          <ExcelExport
            data={s.rows}
            ref={el => this._export = el}
            fileName={"DataverseSearch-" + s.entity + ".xlsx"}
          >
            <Grid
              data={pagedRows}
              skip={s.skip}
              take={s.take}
              total={s.rows.length}
              pageable={{ pageSizes: [25, 50, 100, 200], buttonCount: 5 }}
              sortable={true}
              sort={s.sort}
              onDataStateChange={this.onDataStateChange}
              style={{ minHeight: 400 }}
            >
              <GridToolbar>
                <Button onClick={this.exportExcel}>Export to Excel</Button>
              </GridToolbar>
              {this.renderGridColumns()}
            </Grid>
          </ExcelExport>
        )}

        {s.queryUrl && (
          <details className="dv-query-url">
            <summary>OData query URL (debug)</summary>
            <code>{s.queryUrl}</code>
          </details>
        )}
      </div>
    );
  }
}
